import React, { useState } from "react";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import { useI18n } from "@/i18n";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

export function LeadForm() {
  const { t } = useI18n();
  const l = t.lead;
  const [form, setForm] = useState({
    company_name: "", reference: "", mobile: "", phone: "", email: "", reason: "",
  });
  const [loading, setLoading] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_name.trim() || !form.reference.trim() || !form.email.trim() || !form.mobile.trim() || !form.reason.trim()) {
      toast.error(l.validation);
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads", form);
      toast.success(l.success);
      setForm({ company_name: "", reference: "", mobile: "", phone: "", email: "", reason: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || l.error);
    } finally {
      setLoading(false);
    }
  };

  const fieldCls = "rounded-none border-0 border-b border-white/20 bg-transparent px-0 focus-visible:ring-0 focus-visible:border-gold text-base text-white";

  return (
    <section id="book" className="bg-midnight text-white py-24 md:py-32 grain">
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-20">
        <Reveal className="lg:col-span-5">
          <p className="overline text-champagne">{l.overline}</p>
          <h2 className="mt-4 font-serif text-4xl md:text-6xl tracking-tight leading-[1.02]">{l.title}</h2>
          <p className="mt-6 text-white/60 text-base md:text-lg max-w-md">{l.sub}</p>
          <img
            src="https://images.pexels.com/photos/14914173/pexels-photo-14914173.jpeg"
            alt="Private jet interior"
            className="mt-10 w-full h-64 object-cover hidden lg:block"
          />
        </Reveal>

        <Reveal delay={0.1} className="lg:col-span-7">
          <form onSubmit={submit} className="grid sm:grid-cols-2 gap-x-8 gap-y-7" data-testid="lead-form">
            <Field label={l.companyName}>
              <Input required data-testid="lead-company-name" value={form.company_name} onChange={(e) => set("company_name")(e.target.value)} className={fieldCls} />
            </Field>
            <Field label={l.reference}>
              <Input required data-testid="lead-reference" value={form.reference} onChange={(e) => set("reference")(e.target.value)} className={fieldCls} />
            </Field>
            <Field label={l.mobile}>
              <Input required data-testid="lead-mobile" value={form.mobile} onChange={(e) => set("mobile")(e.target.value)} className={fieldCls} />
            </Field>
            <Field label={l.phone}>
              <Input data-testid="lead-phone" value={form.phone} onChange={(e) => set("phone")(e.target.value)} className={fieldCls} />
            </Field>
            <div className="sm:col-span-2">
              <Field label={l.email}>
                <Input type="email" required data-testid="lead-email" value={form.email} onChange={(e) => set("email")(e.target.value)} className={fieldCls} />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <Field label={l.reason}>
                <Textarea rows={3} required data-testid="lead-reason" value={form.reason} onChange={(e) => set("reason")(e.target.value)} placeholder={l.reasonPlaceholder} className={fieldCls + " resize-none placeholder:text-white/30"} />
              </Field>
            </div>
            <div className="sm:col-span-2 mt-4">
              <button
                type="submit"
                disabled={loading}
                data-testid="lead-submit"
                className="w-full sm:w-auto rounded-full bg-gold px-10 py-4 text-sm font-semibold uppercase tracking-[0.14em] text-midnight transition-transform duration-300 hover:scale-[1.03] disabled:opacity-60"
              >
                {loading ? l.sending : l.submit}
              </button>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <div className="flex flex-col gap-2">
      <Label className="overline text-white/50">{label}</Label>
      {children}
    </div>
  );
}
